import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import VisorComprobante from 'shared/components/VisorComprobante';
import {
  FiArrowLeft,
  FiSave,
  FiUser,
  FiBriefcase,
  FiGlobe,
  FiMapPin,
  FiCreditCard,
  FiEye,
  FiCpu,
  FiX,
  FiEdit3,
  FiRefreshCw,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';
import { C } from 'shared/darkColors';
import { empresaDominio } from 'shared/config';

const EMPTY_EMPRESA = {
  nombre: '',
  slug: '',
  ruc: '',
  telefono: '',
  email_contacto: '',
  tipo_empresa: 'general',
};
const EMPTY_ADMIN = { cedula: '', nombre: '', usuario: '', clave: '', correo: '' };

const TIPO_OPTS = { general: 'General', restaurante: 'Restaurante', importador: 'Importador' };
const TIPO_LABELS = { general: 'General', restaurante: 'Restaurante', importador: 'Importador' };
const TIPO_DESC = {
  general: 'Sin especialización. Todas las opciones básicas disponibles.',
  restaurante: 'Manejo de mesas, cocina, meseros y servicio en sitio.',
  importador: 'Armado de pedidos, catálogo al mayor y entregas.',
};

const parseDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default function EmpresaConfig({ empresaId, onBack }) {
  const [empresa, setEmpresa] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);

  const [empForm, setEmpForm] = useState(EMPTY_EMPRESA);
  const [activa, setActiva] = useState(true);
  const [admForm, setAdmForm] = useState(EMPTY_ADMIN);
  const [adminEditMode, setAdminEditMode] = useState(false);

  const [sedes, setSedes] = useState([]);

  const [suscripcion, setSuscripcion] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [editandoFechaFin, setEditandoFechaFin] = useState(false);
  const [fechaFinVal, setFechaFinVal] = useState('');
  const [visorUrl, setVisorUrl] = useState(null);

  const [aiConfig, setAiConfig] = useState(null);
  const [aiForm, setAiForm] = useState({
    api_key: '',
    modelo: '',
    provider: 'deepseek',
    api_url: '',
    activado: true,
  });
  const [aiKnowledge, setAiKnowledge] = useState({});
  const [aiSkills, setAiSkills] = useState([]);
  const [aiTab, setAiTab] = useState('api');
  const [savingAi, setSavingAi] = useState(false);

  const [mainTab, setMainTab] = useState('info');
  const [skillModal, setSkillModal] = useState(null);
  const [skillForm, setSkillForm] = useState({ titulo: '', contenido: '' });
  const [savingSkill, setSavingSkill] = useState(false);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get(`/super/empresa/${empresaId}/detalle`);
      if (r.data.status === 'success') {
        const d = r.data.data;
        setEmpresa(d.empresa);
        setEmpForm({
          nombre: d.empresa.nombre || '',
          slug: d.empresa.slug || '',
          ruc: d.empresa.ruc || '',
          telefono: d.empresa.telefono || '',
          email_contacto: d.empresa.email_contacto || '',
          tipo_empresa: d.empresa.tipo_empresa || 'general',
        });
        setActiva(d.empresa.activa !== false);
        if (d.admin) {
          setAdmin(d.admin);
          setAdmForm({
            cedula: d.admin.cedula || '',
            nombre: d.admin.nombre || '',
            usuario: d.admin.usuario || '',
            clave: '',
            correo: d.admin.correo || '',
          });
        }
        if (d.suscripcion) setSuscripcion(d.suscripcion);
        if (d.pagos) setPagos(d.pagos);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  const loadSedes = useCallback(async () => {
    try {
      const r = await API.get(`/empresas/${empresaId}/sedes`);
      if (r.data.status === 'success') setSedes(r.data.data);
    } catch {}
  }, [empresaId]);

  const loadAiConfig = useCallback(async () => {
    try {
      const r = await API.get(`/super/empresa/${empresaId}/ai-config`);
      if (r.data.status === 'success') {
        const d = r.data.data;
        setAiConfig(d);
        setAiForm({
          api_key: '',
          modelo: d?.modelo || 'deepseek-v4-flash',
          provider: d?.provider || 'deepseek',
          api_url: d?.api_url || '',
          activado: d?.activado !== false,
        });
        setAiKnowledge({
          prompt_sistema: d?.prompt_sistema || '',
          conocimiento_extra: d?.conocimiento_extra || '',
          fase_bienvenida: d?.fase_bienvenida || '',
          fase_descubrimiento: d?.fase_descubrimiento || '',
          fase_negociacion: d?.fase_negociacion || '',
          fase_cierre: d?.fase_cierre || '',
          fase_postventa: d?.fase_postventa || '',
          pedido_minimo_usd: d?.pedido_minimo_usd ?? '',
          costo_envio_usd: d?.costo_envio_usd ?? '',
          envio_gratis_min_usd: d?.envio_gratis_min_usd ?? '',
          tiempo_entrega_min: d?.tiempo_entrega_min ?? '',
          tiempo_entrega_max: d?.tiempo_entrega_max ?? '',
          horario_semana: d?.horario_semana || '',
          horario_finde: d?.horario_finde || '',
          metodos_pago: d?.metodos_pago || '',
          politicas_devolucion: d?.politicas_devolucion || '',
          zonas_cobertura: d?.zonas_cobertura || '',
          notas_negocio: d?.notas_negocio || '',
          cross_sell_json:
            typeof d?.cross_sell_json === 'string'
              ? d.cross_sell_json
              : JSON.stringify(d?.cross_sell_json || {}, null, 2),
        });
      }
    } catch {}
  }, [empresaId]);

  const loadAiSkills = useCallback(async () => {
    try {
      const r = await API.get(`/super/empresa/${empresaId}/ai-skills`);
      if (r.data.status === 'success') setAiSkills(r.data.data || []);
    } catch {}
  }, [empresaId]);

  const saveAiConfig = async () => {
    if (!aiForm.api_key && !aiConfig?.api_key_masked)
      return showToast('La API key es requerida para la primera configuración', false);
    setSavingAi(true);
    try {
      const payload = {
        modelo: aiForm.modelo || 'deepseek-v4-flash',
        provider: aiForm.provider || 'deepseek',
        api_url: aiForm.api_url || null,
        activado: aiForm.activado,
        prompt_sistema: aiKnowledge.prompt_sistema || null,
        conocimiento_extra: aiKnowledge.conocimiento_extra || null,
        fase_bienvenida: aiKnowledge.fase_bienvenida || null,
        fase_descubrimiento: aiKnowledge.fase_descubrimiento || null,
        fase_negociacion: aiKnowledge.fase_negociacion || null,
        fase_cierre: aiKnowledge.fase_cierre || null,
        fase_postventa: aiKnowledge.fase_postventa || null,
        pedido_minimo_usd:
          aiKnowledge.pedido_minimo_usd !== '' ? parseFloat(aiKnowledge.pedido_minimo_usd) : null,
        costo_envio_usd:
          aiKnowledge.costo_envio_usd !== '' ? parseFloat(aiKnowledge.costo_envio_usd) : null,
        envio_gratis_min_usd:
          aiKnowledge.envio_gratis_min_usd !== ''
            ? parseFloat(aiKnowledge.envio_gratis_min_usd)
            : null,
        tiempo_entrega_min:
          aiKnowledge.tiempo_entrega_min !== '' ? parseInt(aiKnowledge.tiempo_entrega_min) : null,
        tiempo_entrega_max:
          aiKnowledge.tiempo_entrega_max !== '' ? parseInt(aiKnowledge.tiempo_entrega_max) : null,
        horario_semana: aiKnowledge.horario_semana || null,
        horario_finde: aiKnowledge.horario_finde || null,
        metodos_pago: aiKnowledge.metodos_pago || null,
        politicas_devolucion: aiKnowledge.politicas_devolucion || null,
        zonas_cobertura: aiKnowledge.zonas_cobertura || null,
        notas_negocio: aiKnowledge.notas_negocio || null,
        cross_sell_json: aiKnowledge.cross_sell_json || null,
      };
      if (aiForm.api_key) payload.api_key = aiForm.api_key;
      await API.put(`/super/empresa/${empresaId}/ai-config`, payload);
      showToast('Configuración de IA guardada');
      loadAiConfig();
      setAiForm((prev) => ({ ...prev, api_key: '' }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al guardar configuración de IA';
      showToast(msg, false);
    } finally {
      setSavingAi(false);
    }
  };

  const openSkillModal = (skill) => {
    if (skill) {
      setSkillModal(skill.id ? 'edit' : 'create');
      setSkillForm({
        titulo: skill.titulo || '',
        contenido: skill.contenido || '',
        id: skill.id || null,
        source_key: skill.source_key || null,
        es_base: skill.es_base || false,
      });
    } else {
      setSkillModal('create');
      setSkillForm({ titulo: '', contenido: '', id: null, source_key: null, es_base: false });
    }
  };

  const saveSkill = async () => {
    if (!skillForm.titulo || !skillForm.contenido)
      return showToast('Titulo y contenido requeridos', false);
    setSavingSkill(true);
    try {
      const payload = { titulo: skillForm.titulo, contenido: skillForm.contenido };
      if (skillForm.id) {
        await API.put(`/super/empresa/${empresaId}/ai-skills/${skillForm.id}`, payload);
      } else if (skillForm.source_key) {
        payload.source_key = skillForm.source_key;
        payload.es_base = true;
        await API.post(`/super/empresa/${empresaId}/ai-skills`, payload);
      } else {
        await API.post(`/super/empresa/${empresaId}/ai-skills`, payload);
      }
      showToast(skillForm.id || skillForm.source_key ? 'Skill actualizado' : 'Skill creado');
      setSkillModal(null);
      setSkillForm({ titulo: '', contenido: '', id: null, source_key: null, es_base: false });
      loadAiSkills();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Error al guardar skill', false);
    } finally {
      setSavingSkill(false);
    }
  };

  const toggleSkill = async (skill) => {
    if (!skill.id) {
      const payload = {
        titulo: skill.titulo,
        contenido: skill.contenido,
        source_key: skill.source_key,
        es_base: true,
        activo: !skill.activo,
      };
      await API.post(`/super/empresa/${empresaId}/ai-skills`, payload);
    } else {
      await API.put(`/super/empresa/${empresaId}/ai-skills/${skill.id}`, {
        titulo: skill.titulo,
        contenido: skill.contenido,
        activo: !skill.activo,
      });
    }
    loadAiSkills();
  };

  const deleteSkill = async (skill) => {
    const msg = skill.es_base ? 'Restaurar al default del sistema?' : 'Eliminar este skill?';
    if (!window.confirm(msg)) return;
    try {
      if (skill.id) {
        await API.delete(`/super/empresa/${empresaId}/ai-skills/${skill.id}`);
      }
      loadAiSkills();
    } catch {}
  };

  const resetSkill = async (skill) => {
    if (!skill.id || !skill.es_base) return;
    if (!window.confirm('Restaurar al contenido default del sistema?')) return;
    try {
      await API.delete(`/super/empresa/${empresaId}/ai-skills/${skill.id}`);
      loadAiSkills();
    } catch {}
  };

  useEffect(() => {
    loadData();
    loadSedes();
    loadAiConfig();
    loadAiSkills();
  }, [loadData, loadSedes, loadAiConfig, loadAiSkills]);

  const saveEmpresa = async () => {
    if (!empForm.nombre || !empForm.slug) return showToast('Nombre y slug requeridos', false);
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('nombre', empForm.nombre);
      params.append('slug', empForm.slug);
      params.append('ruc', empForm.ruc || '');
      params.append('activa', activa);
      params.append('telefono', empForm.telefono || '');
      params.append('email_contacto', empForm.email_contacto || '');
      await API.put(`/empresas/modificar/${empresaId}?${params.toString()}`);
      showToast('Datos de la empresa guardados');
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al guardar';
      showToast(msg, false);
    } finally {
      setSaving(false);
    }
  };

  const saveAdmin = async () => {
    if (!admForm.cedula || !admForm.nombre || !admForm.usuario)
      return showToast('Cedula, nombre y usuario requeridos', false);
    if (!admin && !admForm.clave)
      return showToast('La clave es requerida para crear el admin', false);
    setSavingAdmin(true);
    try {
      const payload = { cedula: admForm.cedula, nombre: admForm.nombre, usuario: admForm.usuario };
      if (admForm.clave) payload.clave = admForm.clave;
      if (admForm.correo) payload.correo = admForm.correo;
      await API.put(`/super/empresa/${empresaId}/admin`, payload);
      showToast(admin ? 'Admin actualizado' : 'Admin creado');
      setAdminEditMode(false);
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al guardar admin';
      showToast(msg, false);
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleActualizarVencimiento = async () => {
    if (!fechaFinVal) return showToast('Selecciona una fecha', false);
    try {
      const params = new URLSearchParams();
      params.append('fecha_fin', fechaFinVal);
      await API.put(`/super/empresa/${empresaId}/vencimiento?${params.toString()}`);
      showToast('Vencimiento actualizado');
      setEditandoFechaFin(false);
      loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Error al actualizar vencimiento';
      showToast(msg, false);
    }
  };

  if (loading) return <div style={C.loading}>Cargando...</div>;

  const mainTabs = [
    { id: 'info', icon: FiBriefcase, label: 'Informacion' },
    { id: 'sedes', icon: FiMapPin, label: `Sedes (${sedes.length})` },
    { id: 'plan', icon: FiCreditCard, label: 'Plan' },
    { id: 'ia', icon: FiCpu, label: 'IA' },
    { id: 'admin', icon: FiUser, label: 'Admin' },
  ];

  return (
    <div className="ec-wrapper" style={{ maxWidth: '1240px', margin: '0 auto', padding: '24px' }}>
      {visorUrl && <VisorComprobante comprobanteUrl={visorUrl} onClose={() => setVisorUrl(null)} />}
      {toast && <div style={C.toast}>{toast.msg}</div>}

      <style>{`
        @keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 768px) {
          .ec-wrapper { padding: 12px !important; }
          .ec-card { padding: 18px !important; border-radius: 12px !important; }
          .ec-form-row { grid-template-columns: 1fr !important; }
          .ec-tabs { overflow-x: auto; gap: 2px !important; }
          .ec-tab-btn { padding: 10px 14px !important; font-size: 12px !important; }
          .ec-stats { grid-template-columns: 1fr 1fr !important; }
          .ec-plan-grid { grid-template-columns: 1fr !important; }
          .ec-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .ec-table-wrap table { min-width: 600px; }
        }
      `}</style>

      {/* === HEADER === */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}
      >
        <button onClick={onBack} style={{ ...C.btnOutline, flexShrink: 0, padding: '9px 16px' }}>
          <FiArrowLeft size={18} />
          <span style={{ marginLeft: '6px' }}>Volver</span>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 800,
              color: '#111827',
              margin: 0,
              letterSpacing: '-0.3px',
            }}
          >
            {empresa?.nombre || 'Empresa'}
          </h1>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '4px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: 500 }}>
              <FiGlobe size={11} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
              {empresa?.slug ? empresaDominio(empresa.slug) : ''}
            </span>
            {empresa?.origen_registro === 'auto' && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  background: '#f5f3ff',
                  color: '#7c3aed',
                  padding: '3px 10px',
                  borderRadius: '6px',
                }}
              >
                AUTO
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 16px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.2px',
              background: empresa?.activa !== false ? '#ecfdf5' : '#fef2f2',
              color: empresa?.activa !== false ? '#059669' : '#dc2626',
              border: `1.5px solid ${empresa?.activa !== false ? '#a7f3d0' : '#fecaca'}`,
            }}
          >
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: empresa?.activa !== false ? '#059669' : '#dc2626',
              }}
            />
            {empresa?.activa !== false ? 'Activa' : 'Inactiva'}
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '7px 14px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: 600,
              background: '#f9fafb',
              color: '#374151',
              border: '1.5px solid #e5e7eb',
            }}
          >
            <FiBriefcase size={12} style={{ marginRight: '5px' }} />
            {TIPO_LABELS[empForm.tipo_empresa] || empForm.tipo_empresa}
          </span>
        </div>
      </div>

      {/* === STATS BAR === */}
      <div
        className="ec-stats"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
          marginBottom: '24px',
        }}
      >
        {[
          { label: 'Sedes', value: sedes.length, color: '#2563eb', bg: '#eff6ff' },
          {
            label: 'Plan',
            value: suscripcion?.plan_nombre || 'Sin plan',
            color: '#059669',
            bg: '#ecfdf5',
          },
          {
            label: 'Vencimiento',
            value: suscripcion?.fecha_fin
              ? parseDate(suscripcion.fecha_fin).toLocaleDateString()
              : '--',
            color:
              suscripcion?.fecha_fin &&
              parseDate(suscripcion.fecha_fin) < new Date(new Date().toDateString())
                ? '#dc2626'
                : '#111827',
            bg:
              suscripcion?.fecha_fin &&
              parseDate(suscripcion.fecha_fin) < new Date(new Date().toDateString())
                ? '#fef2f2'
                : '#f9fafb',
          },
          {
            label: 'IA',
            value: aiConfig?.activado !== false ? 'Activada' : 'Inactiva',
            color: aiConfig?.activado !== false ? '#7c3aed' : '#9ca3af',
            bg: aiConfig?.activado !== false ? '#f5f3ff' : '#f9fafb',
          },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: stat.bg,
              borderRadius: '12px',
              padding: '14px 18px',
              border: `1px solid ${stat.bg === '#f9fafb' ? '#f3f4f6' : stat.bg}`,
            }}
          >
            <div
              style={{
                fontSize: '11px',
                color: '#9ca3af',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
              }}
            >
              {stat.label}
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* === MAIN TABS === */}
      <div
        className="ec-tabs"
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          borderBottom: '2px solid #f3f4f6',
          paddingBottom: '0',
          overflowX: 'auto',
        }}
      >
        {mainTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setMainTab(tab.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 22px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: mainTab === tab.id ? 700 : 500,
              color: mainTab === tab.id ? '#111827' : '#9ca3af',
              borderBottom: mainTab === tab.id ? '3px solid #2563eb' : '3px solid transparent',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              marginBottom: '-2px',
            }}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* === TAB: INFO === */}
      {mainTab === 'info' && (
        <div
          className="ec-card"
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '28px 32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
            marginBottom: '24px',
            border: '1px solid #f3f4f6',
          }}
        >
          <div style={{ ...C.cardTitle, marginBottom: '24px', fontSize: '16px', fontWeight: 800 }}>
            <FiBriefcase size={18} color="#111827" /> Datos de la Empresa
          </div>
          <div className="ec-form-row" style={C.formRow}>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Nombre *</label>
              <input
                value={empForm.nombre}
                onChange={(e) => setEmpForm({ ...empForm, nombre: e.target.value })}
                style={C.formInput}
              />
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Slug</label>
              <input
                value={empForm.slug}
                onChange={(e) =>
                  setEmpForm({
                    ...empForm,
                    slug: e.target.value.replace(/\s+/g, '-').toLowerCase(),
                  })
                }
                style={C.formInput}
              />
            </div>
          </div>
          <div className="ec-form-row" style={C.formRow}>
            <div style={C.formGroup}>
              <label style={C.formLabel}>RUC</label>
              <input
                value={empForm.ruc}
                onChange={(e) => setEmpForm({ ...empForm, ruc: e.target.value })}
                style={C.formInput}
              />
            </div>
            <div style={C.formGroup}>
              <label style={C.formLabel}>Telefono</label>
              <input
                value={empForm.telefono}
                onChange={(e) => setEmpForm({ ...empForm, telefono: e.target.value })}
                style={C.formInput}
              />
            </div>
          </div>
          <div style={C.formGroup}>
            <label style={C.formLabel}>Email de contacto</label>
            <input
              type="email"
              value={empForm.email_contacto}
              onChange={(e) => setEmpForm({ ...empForm, email_contacto: e.target.value })}
              style={C.formInput}
            />
          </div>

          <div style={{ borderTop: '1.5px solid #f3f4f6', margin: '20px 0', paddingTop: '20px' }}>
            <div
              style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '14px' }}
            >
              Configuracion del tipo de empresa
            </div>
            <div style={C.formRow}>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Tipo de empresa</label>
                <select
                  value={empForm.tipo_empresa}
                  onChange={async (e) => {
                    const nuevoTipo = e.target.value;
                    setEmpForm({ ...empForm, tipo_empresa: nuevoTipo });
                    try {
                      const params = new URLSearchParams();
                      params.append('nombre', empForm.nombre);
                      params.append('slug', empForm.slug);
                      params.append('ruc', empForm.ruc || '');
                      params.append('activa', activa);
                      params.append('telefono', empForm.telefono || '');
                      params.append('email_contacto', empForm.email_contacto || '');
                      params.append('tipo_empresa', nuevoTipo);
                      await API.put(`/empresas/modificar/${empresaId}?${params.toString()}`);
                      showToast(`Tipo cambiado a "${TIPO_LABELS[nuevoTipo]}"`);
                      loadData();
                    } catch (err) {
                      showToast(err?.response?.data?.detail || 'Error al cambiar tipo', false);
                    }
                  }}
                  style={C.formSelect}
                >
                  {Object.entries(TIPO_OPTS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  ...C.formGroup,
                  display: 'flex',
                  alignItems: 'flex-end',
                  paddingBottom: '2px',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    lineHeight: 1.5,
                    background: '#f9fafb',
                    borderRadius: '8px',
                    padding: '10px 14px',
                  }}
                >
                  {TIPO_DESC[empForm.tipo_empresa] || ''}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderTop: '1.5px solid #f3f4f6',
              paddingTop: '20px',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                color: '#374151',
              }}
            >
              <input
                type="checkbox"
                checked={activa}
                onChange={(e) => setActiva(e.target.checked)}
                style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#2563eb' }}
              />
              Empresa activa
            </label>
            <button
              onClick={saveEmpresa}
              disabled={saving}
              style={{ ...C.btnPrimary, marginLeft: 'auto', opacity: saving ? 0.6 : 1 }}
            >
              <FiSave size={14} style={{ marginRight: '6px' }} />
              Guardar
            </button>
          </div>
        </div>
      )}

      {/* === TAB: SEDES === */}
      {mainTab === 'sedes' && (
        <div
          className="ec-card"
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '28px 32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
            marginBottom: '24px',
            border: '1px solid #f3f4f6',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '22px',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                fontWeight: 800,
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <FiMapPin size={18} color="#111827" /> Sedes ({sedes.length})
            </div>
          </div>
          <div className="ec-table-wrap">
            <table style={C.table}>
              <thead>
                <tr>
                  <th style={C.th}>Codigo</th>
                  <th style={C.th}>Nombre</th>
                  <th style={C.th}>Supervisor</th>
                  <th style={C.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {sedes.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={C.emptyRow}>
                      No hay sedes registradas para esta empresa
                    </td>
                  </tr>
                ) : (
                  sedes.map((s) => (
                    <tr key={s.codigo}>
                      <td style={C.td}>{s.codigo}</td>
                      <td style={{ ...C.td, fontWeight: 600 }}>{s.nombre}</td>
                      <td style={C.td}>
                        {s.supervisor_nombre ||
                          (s.supervisor_cedula ? `(${s.supervisor_cedula})` : '--')}
                      </td>
                      <td style={C.td}>
                        <span
                          style={{
                            ...C.badge,
                            ...(s.activa !== false ? C.badgeGreen : C.badgeGray),
                          }}
                        >
                          {s.activa !== false ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === TAB: PLAN === */}
      {mainTab === 'plan' && (
        <div
          className="ec-card"
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '28px 32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
            marginBottom: '24px',
            border: '1px solid #f3f4f6',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: 800,
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '24px',
            }}
          >
            <FiCreditCard size={18} color="#111827" /> Plan y Suscripcion
          </div>

          {suscripcion?.mensajes_ia_limite != null && (
            <div
              style={{
                background: '#f5f3ff',
                borderRadius: '12px',
                padding: '16px 20px',
                border: '1.5px solid #ddd6fe',
                marginBottom: '20px',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}
              >
                <span
                  style={{
                    ...C.badge,
                    background: '#7c3aed',
                    color: '#fff',
                    padding: '4px 14px',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  Plan Gratuito
                </span>
                <span style={{ fontSize: '12px', color: '#6b21a8' }}>
                  Trial 14 dias — {suscripcion.mensajes_ia_limite} mensajes IA incluidos
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    flex: 1,
                    height: '8px',
                    borderRadius: '4px',
                    background: '#ede9fe',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, ((suscripcion.mensajes_ia_usados || 0) / suscripcion.mensajes_ia_limite) * 100)}%`,
                      height: '100%',
                      borderRadius: '4px',
                      background:
                        (suscripcion.mensajes_ia_usados || 0) >= suscripcion.mensajes_ia_limite
                          ? '#dc2626'
                          : '#7c3aed',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color:
                      (suscripcion.mensajes_ia_usados || 0) >= suscripcion.mensajes_ia_limite
                        ? '#dc2626'
                        : '#6b21a8',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {suscripcion.mensajes_ia_usados || 0} / {suscripcion.mensajes_ia_limite}
                </span>
              </div>
              {(suscripcion.mensajes_ia_usados || 0) >= suscripcion.mensajes_ia_limite && (
                <div
                  style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600, marginTop: '8px' }}
                >
                  Limite alcanzado — el bot ya no responde, los chats pasan a atencion humana
                </div>
              )}
            </div>
          )}

          {suscripcion ? (
            <div
              className="ec-plan-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
              }}
            >
              {[
                {
                  label: 'Plan actual',
                  value: suscripcion.plan_nombre,
                  sub: suscripcion.plan_tipo
                    ? `Inventario ${suscripcion.plan_tipo.charAt(0).toUpperCase() + suscripcion.plan_tipo.slice(1)}`
                    : null,
                },
                { label: 'Precio mensual', value: `$${suscripcion.plan_precio}`, color: '#059669' },
              ].map((item) => (
                <div key={item.label}>
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      fontWeight: 600,
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{ fontSize: '17px', fontWeight: 700, color: item.color || '#111827' }}
                  >
                    {item.value}
                  </div>
                  {item.sub && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {item.sub}
                    </div>
                  )}
                </div>
              ))}
              <div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#9ca3af',
                    fontWeight: 600,
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Vencimiento
                </div>
                {editandoFechaFin ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={fechaFinVal}
                      onChange={(e) => setFechaFinVal(e.target.value)}
                      style={{
                        ...C.formInput,
                        width: '150px',
                        padding: '7px 10px',
                        fontSize: '12px',
                      }}
                    />
                    <button
                      onClick={handleActualizarVencimiento}
                      style={{ ...C.btnSm, background: '#059669', color: '#fff' }}
                    >
                      OK
                    </button>
                    <button
                      onClick={() => setEditandoFechaFin(false)}
                      style={{ ...C.btnSm, background: '#f3f4f6', color: '#6b7280' }}
                    >
                      X
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div
                      style={{
                        fontSize: '17px',
                        fontWeight: 700,
                        color:
                          suscripcion.fecha_fin &&
                          parseDate(suscripcion.fecha_fin) < new Date(new Date().toDateString())
                            ? '#dc2626'
                            : '#111827',
                      }}
                    >
                      {suscripcion.fecha_fin
                        ? parseDate(suscripcion.fecha_fin).toLocaleDateString()
                        : '--'}
                    </div>
                    <button
                      onClick={() => {
                        setFechaFinVal(suscripcion?.fecha_fin?.split('T')[0] || '');
                        setEditandoFechaFin(true);
                      }}
                      style={{ ...C.btnSm, background: '#eff6ff', color: '#2563eb' }}
                    >
                      Editar
                    </button>
                  </div>
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#9ca3af',
                    fontWeight: 600,
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Estado
                </div>
                <span
                  style={{
                    display: 'inline-flex',
                    padding: '5px 14px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    background:
                      suscripcion.fecha_fin &&
                      parseDate(suscripcion.fecha_fin) >= new Date(new Date().toDateString())
                        ? '#ecfdf5'
                        : '#fef2f2',
                    color:
                      suscripcion.fecha_fin &&
                      parseDate(suscripcion.fecha_fin) >= new Date(new Date().toDateString())
                        ? '#059669'
                        : '#dc2626',
                  }}
                >
                  {suscripcion.fecha_fin &&
                  parseDate(suscripcion.fecha_fin) >= new Date(new Date().toDateString())
                    ? 'Al dia'
                    : 'Vencido'}
                </span>
              </div>
            </div>
          ) : (
            <div
              style={{
                fontSize: '13px',
                color: '#9ca3af',
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#f9fafb',
                borderRadius: '10px',
              }}
            >
              Sin plan asignado.
            </div>
          )}

          <div style={{ borderTop: '1.5px solid #f3f4f6', paddingTop: '22px' }}>
            <div
              style={{ fontSize: '13px', fontWeight: 700, color: '#111827', marginBottom: '14px' }}
            >
              Historial de pagos
            </div>
            {pagos.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#9ca3af', padding: '12px 0' }}>
                No hay pagos registrados
              </div>
            ) : (
              <div className="ec-table-wrap">
                <table style={C.table}>
                  <thead>
                    <tr>
                      <th style={C.th}>Fecha</th>
                      <th style={C.th}>Monto</th>
                      <th style={C.th}>Metodo</th>
                      <th style={C.th}>Ref.</th>
                      <th style={C.th}>Comprobante</th>
                      <th style={C.th}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagos.map((p) => (
                      <tr key={p.id}>
                        <td style={C.td}>
                          {p.fecha_pago ? new Date(p.fecha_pago + 'Z').toLocaleDateString() : '--'}
                        </td>
                        <td style={{ ...C.td, fontWeight: 700, color: '#059669' }}>${p.monto}</td>
                        <td style={C.td}>{p.metodo || '--'}</td>
                        <td style={C.td}>{p.referencia || '--'}</td>
                        <td style={C.td}>
                          {p.comprobante_url ? (
                            <button
                              onClick={() => setVisorUrl(p.comprobante_url)}
                              style={{ ...C.btnSm, background: '#eff6ff', color: '#2563eb' }}
                            >
                              <FiEye size={12} style={{ marginRight: '4px' }} />
                              Ver
                            </button>
                          ) : (
                            '--'
                          )}
                        </td>
                        <td style={C.td}>
                          <span
                            style={{
                              display: 'inline-flex',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background:
                                p.estado === 'verificado'
                                  ? '#ecfdf5'
                                  : p.estado === 'pendiente'
                                    ? '#fffbeb'
                                    : p.estado === 'rechazado'
                                      ? '#fef2f2'
                                      : '#f3f4f6',
                              color:
                                p.estado === 'verificado'
                                  ? '#059669'
                                  : p.estado === 'pendiente'
                                    ? '#d97706'
                                    : p.estado === 'rechazado'
                                      ? '#dc2626'
                                      : '#6b7280',
                            }}
                          >
                            {p.estado || 'pendiente'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === TAB: IA === */}
      {mainTab === 'ia' && (
        <div
          className="ec-card"
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '28px 32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
            marginBottom: '24px',
            border: '1px solid #f3f4f6',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: 800,
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '20px',
            }}
          >
            <FiCpu size={18} color="#111827" /> Personalidad de IA
          </div>

          {aiConfig ? (
            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '18px',
                flexWrap: 'wrap',
                alignItems: 'center',
                fontSize: '12px',
                color: '#6b7280',
                background: '#f9fafb',
                borderRadius: '10px',
                padding: '10px 16px',
              }}
            >
              <span>
                API: <strong style={{ color: '#111827' }}>{aiConfig.api_key_masked || '--'}</strong>
              </span>
              <span style={{ color: '#d1d5db' }}>|</span>
              <span>
                <strong style={{ color: '#111827' }}>
                  {aiConfig.provider === 'openai'
                    ? 'OpenAI'
                    : aiConfig.provider === 'anthropic'
                      ? 'Anthropic'
                      : 'DeepSeek'}
                </strong>
              </span>
              <span style={{ color: '#d1d5db' }}>|</span>
              <span>
                <strong style={{ color: '#111827' }}>{aiConfig.modelo || '--'}</strong>
              </span>
              <span style={{ marginLeft: 'auto' }}>
                <span
                  style={{
                    ...C.badge,
                    ...(aiConfig.activado !== false ? C.badgeGreen : C.badgeGray),
                  }}
                >
                  {aiConfig.activado !== false ? 'Activado' : 'Desactivado'}
                </span>
              </span>
            </div>
          ) : (
            <div
              style={{
                fontSize: '13px',
                color: '#9ca3af',
                marginBottom: '14px',
                padding: '10px 16px',
                background: '#f9fafb',
                borderRadius: '10px',
              }}
            >
              Sin configuracion. La empresa usara la API key global y defaults.
            </div>
          )}

          {/* Subtabs IA */}
          <div
            style={{
              display: 'flex',
              borderBottom: '2px solid #f3f4f6',
              marginBottom: '20px',
              overflowX: 'auto',
              gap: '0',
            }}
          >
            {['api', 'prompt', 'fases', 'politicas', 'conocimiento', 'cross'].map((tab) => (
              <button
                key={tab}
                onClick={() => setAiTab(tab)}
                style={{
                  padding: '10px 18px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: aiTab === tab ? 600 : 400,
                  color: aiTab === tab ? '#111827' : '#9ca3af',
                  borderBottom: aiTab === tab ? '2.5px solid #2563eb' : '2.5px solid transparent',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
              >
                {tab === 'api'
                  ? 'API Key'
                  : tab === 'prompt'
                    ? 'Prompt'
                    : tab === 'fases'
                      ? 'Fases'
                      : tab === 'politicas'
                        ? 'Politicas'
                        : tab === 'cross'
                          ? 'Cross-sell'
                          : 'Conocimiento'}
              </button>
            ))}
          </div>

          {aiTab === 'api' && (
            <div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Proveedor</label>
                  <select
                    value={aiForm.provider}
                    onChange={(e) => setAiForm({ ...aiForm, provider: e.target.value })}
                    style={C.formSelect}
                  >
                    <option value="deepseek">DeepSeek</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Modelo</label>
                  <input
                    value={aiForm.modelo}
                    onChange={(e) => setAiForm({ ...aiForm, modelo: e.target.value })}
                    style={C.formInput}
                    placeholder="deepseek-v4-flash"
                  />
                </div>
              </div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>
                    API Key {!aiConfig ? '*' : '(dejar vacio para mantener)'}
                  </label>
                  <input
                    type="password"
                    value={aiForm.api_key}
                    onChange={(e) => setAiForm({ ...aiForm, api_key: e.target.value })}
                    style={C.formInput}
                    placeholder={aiConfig ? '••••••••••••••••' : 'sk-...'}
                    autoComplete="off"
                  />
                </div>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>URL del API (opcional)</label>
                  <input
                    value={aiForm.api_url}
                    onChange={(e) => setAiForm({ ...aiForm, api_url: e.target.value })}
                    style={C.formInput}
                    placeholder="https://api.deepseek.com/v1/chat/completions"
                  />
                </div>
              </div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  marginBottom: '10px',
                }}
              >
                <input
                  type="checkbox"
                  checked={aiForm.activado}
                  onChange={(e) => setAiForm({ ...aiForm, activado: e.target.checked })}
                  style={{
                    width: '17px',
                    height: '17px',
                    cursor: 'pointer',
                    accentColor: '#2563eb',
                  }}
                />
                Activado
              </label>
            </div>
          )}

          {aiTab === 'prompt' && (
            <div>
              <label style={C.formLabel}>
                Prompt del sistema (usa {'{nombre_bot}'}, {'{nombre_sede}'}, {'{nombre_cliente}'})
              </label>
              <textarea
                rows={12}
                value={aiKnowledge.prompt_sistema}
                onChange={(e) => setAiKnowledge({ ...aiKnowledge, prompt_sistema: e.target.value })}
                style={{
                  ...C.formInput,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  resize: 'vertical',
                  minHeight: '220px',
                  lineHeight: '1.6',
                }}
                placeholder="Eres {nombre_bot}, vendedor experto..."
              />
            </div>
          )}

          {aiTab === 'fases' && (
            <div>
              {[
                { key: 'fase_bienvenida', label: 'Bienvenida' },
                { key: 'fase_descubrimiento', label: 'Descubrimiento' },
                { key: 'fase_negociacion', label: 'Negociacion' },
                { key: 'fase_cierre', label: 'Cierre' },
                { key: 'fase_postventa', label: 'Post-Venta' },
              ].map((f) => (
                <div key={f.key} style={{ marginBottom: '14px' }}>
                  <label
                    style={{ ...C.formLabel, fontWeight: 700, fontSize: '12px', color: '#374151' }}
                  >
                    {f.label}
                  </label>
                  <textarea
                    rows={4}
                    value={aiKnowledge[f.key]}
                    onChange={(e) => setAiKnowledge({ ...aiKnowledge, [f.key]: e.target.value })}
                    style={{
                      ...C.formInput,
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      resize: 'vertical',
                    }}
                    placeholder={`Instrucciones para la fase de ${f.label}...`}
                  />
                </div>
              ))}
            </div>
          )}

          {aiTab === 'politicas' && (
            <div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Pedido minimo (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={aiKnowledge.pedido_minimo_usd}
                    onChange={(e) =>
                      setAiKnowledge({ ...aiKnowledge, pedido_minimo_usd: e.target.value })
                    }
                    style={C.formInput}
                    placeholder="3.00"
                  />
                </div>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Costo de envio (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={aiKnowledge.costo_envio_usd}
                    onChange={(e) =>
                      setAiKnowledge({ ...aiKnowledge, costo_envio_usd: e.target.value })
                    }
                    style={C.formInput}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Envio gratis desde (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={aiKnowledge.envio_gratis_min_usd}
                    onChange={(e) =>
                      setAiKnowledge({ ...aiKnowledge, envio_gratis_min_usd: e.target.value })
                    }
                    style={C.formInput}
                    placeholder="50.00"
                  />
                </div>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Tiempo entrega (min)</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      value={aiKnowledge.tiempo_entrega_min}
                      onChange={(e) =>
                        setAiKnowledge({ ...aiKnowledge, tiempo_entrega_min: e.target.value })
                      }
                      style={C.formInput}
                      placeholder="45"
                    />
                    <input
                      type="number"
                      value={aiKnowledge.tiempo_entrega_max}
                      onChange={(e) =>
                        setAiKnowledge({ ...aiKnowledge, tiempo_entrega_max: e.target.value })
                      }
                      style={C.formInput}
                      placeholder="90"
                    />
                  </div>
                </div>
              </div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Horario semana</label>
                  <input
                    value={aiKnowledge.horario_semana}
                    onChange={(e) =>
                      setAiKnowledge({ ...aiKnowledge, horario_semana: e.target.value })
                    }
                    style={C.formInput}
                    placeholder="Lun-Vie 8AM-5PM"
                  />
                </div>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Horario finde</label>
                  <input
                    value={aiKnowledge.horario_finde}
                    onChange={(e) =>
                      setAiKnowledge({ ...aiKnowledge, horario_finde: e.target.value })
                    }
                    style={C.formInput}
                    placeholder="Sab 8AM-2PM"
                  />
                </div>
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Metodos de pago</label>
                <textarea
                  rows={2}
                  value={aiKnowledge.metodos_pago}
                  onChange={(e) => setAiKnowledge({ ...aiKnowledge, metodos_pago: e.target.value })}
                  style={{ ...C.formInput, resize: 'vertical' }}
                  placeholder="PagoMovil, Efectivo, Zelle, Transferencia"
                />
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Politicas de devolucion</label>
                <textarea
                  rows={2}
                  value={aiKnowledge.politicas_devolucion}
                  onChange={(e) =>
                    setAiKnowledge({ ...aiKnowledge, politicas_devolucion: e.target.value })
                  }
                  style={{ ...C.formInput, resize: 'vertical' }}
                  placeholder="Cambios y devoluciones en 24h..."
                />
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Zonas de cobertura</label>
                <textarea
                  rows={2}
                  value={aiKnowledge.zonas_cobertura}
                  onChange={(e) =>
                    setAiKnowledge({ ...aiKnowledge, zonas_cobertura: e.target.value })
                  }
                  style={{ ...C.formInput, resize: 'vertical' }}
                  placeholder="Caracas area metropolitana"
                />
              </div>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Notas del negocio</label>
                <textarea
                  rows={3}
                  value={aiKnowledge.notas_negocio}
                  onChange={(e) =>
                    setAiKnowledge({ ...aiKnowledge, notas_negocio: e.target.value })
                  }
                  style={{ ...C.formInput, resize: 'vertical' }}
                  placeholder="Cualquier informacion adicional relevante..."
                />
              </div>
            </div>
          )}

          {aiTab === 'conocimiento' && (
            <div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}
              >
                <label style={{ fontSize: '14px', fontWeight: 700, color: '#111827', flex: 1 }}>
                  Skills de conocimiento ({aiSkills.length})
                </label>
                <button
                  onClick={() => openSkillModal(null)}
                  style={{
                    ...C.btnSm,
                    background: '#111827',
                    color: '#fff',
                    padding: '7px 16px',
                    fontSize: '12px',
                  }}
                >
                  <FiPlus size={13} style={{ marginRight: '5px' }} />
                  Nuevo skill
                </button>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {aiSkills.filter((s) => s._overridden).length} modificados
                </span>
              </div>

              {aiSkills.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#9ca3af', padding: '16px 0' }}>
                  No hay skills cargados. Crea uno nuevo para enriquecer el conocimiento del bot.
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    marginBottom: '20px',
                  }}
                >
                  {aiSkills.map((skill) => (
                    <div
                      key={skill.id || skill.source_key}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        background: '#fafbfc',
                        border: '1px solid #f3f4f6',
                        opacity: skill.activo === false ? 0.45 : 1,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '2px',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={skill.activo !== false}
                            onChange={() => toggleSkill(skill)}
                            style={{
                              width: '15px',
                              height: '15px',
                              cursor: 'pointer',
                              flexShrink: 0,
                              accentColor: '#2563eb',
                            }}
                          />
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#111827',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {skill.titulo}
                          </span>
                          {skill.es_base ? (
                            <span style={C.badgeBlue}>base</span>
                          ) : (
                            <span style={{ ...C.badge, background: '#ecfdf5', color: '#059669' }}>
                              custom
                            </span>
                          )}
                          {skill._overridden && (
                            <span style={{ ...C.badge, background: '#fffbeb', color: '#d97706' }}>
                              modificado
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            marginLeft: '23px',
                            maxHeight: '52px',
                            overflow: 'hidden',
                          }}
                        >
                          {skill.contenido?.substring(0, 140)}
                          {skill.contenido?.length > 140 ? '...' : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button
                          onClick={() => openSkillModal(skill)}
                          style={{
                            ...C.btnSm,
                            background: '#eff6ff',
                            color: '#2563eb',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <FiEdit3 size={11} />
                          Editar
                        </button>
                        {skill._overridden ? (
                          <button
                            onClick={() => resetSkill(skill)}
                            style={{
                              ...C.btnSm,
                              background: '#fffbeb',
                              color: '#d97706',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <FiRefreshCw size={11} />
                            Reset
                          </button>
                        ) : (
                          <button
                            onClick={() => deleteSkill(skill)}
                            style={{
                              ...C.btnSm,
                              background: '#fef2f2',
                              color: '#dc2626',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <FiTrash2 size={11} />X
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ borderTop: '1.5px solid #f3f4f6', paddingTop: '18px' }}>
                <label
                  style={{ ...C.formLabel, fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}
                >
                  Conocimiento Extra (texto libre adicional)
                </label>
                <textarea
                  rows={6}
                  value={aiKnowledge.conocimiento_extra}
                  onChange={(e) =>
                    setAiKnowledge({ ...aiKnowledge, conocimiento_extra: e.target.value })
                  }
                  style={{
                    ...C.formInput,
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    resize: 'vertical',
                    minHeight: '110px',
                  }}
                  placeholder="Texto adicional que se agrega al final del conocimiento..."
                />
              </div>
            </div>
          )}

          {aiTab === 'cross' && (
            <div>
              <label style={C.formLabel}>
                Reglas de Cross-sell (JSON: {'{'}producto:['complemento1','complemento2']{'}'})
              </label>
              <textarea
                rows={12}
                value={aiKnowledge.cross_sell_json}
                onChange={(e) =>
                  setAiKnowledge({ ...aiKnowledge, cross_sell_json: e.target.value })
                }
                style={{
                  ...C.formInput,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  resize: 'vertical',
                  minHeight: '220px',
                }}
                placeholder='{"harina":["queso","jamon"],"arroz":["caraotas","pollo"]}'
              />
            </div>
          )}

          <div
            style={{
              borderTop: '1.5px solid #f3f4f6',
              paddingTop: '16px',
              marginTop: '16px',
              display: 'flex',
              gap: '10px',
            }}
          >
            <button
              onClick={saveAiConfig}
              disabled={savingAi}
              style={{ ...C.btnPrimary, opacity: savingAi ? 0.6 : 1 }}
            >
              <FiSave size={14} style={{ marginRight: '6px' }} />
              Guardar configuracion IA
            </button>
          </div>
        </div>
      )}

      {/* === TAB: ADMIN === */}
      {mainTab === 'admin' && (
        <div
          className="ec-card"
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '28px 32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
            marginBottom: '24px',
            border: '1px solid #f3f4f6',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: 800,
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '24px',
            }}
          >
            <FiUser size={18} color="#111827" /> Usuario Administrador
          </div>

          {!adminEditMode && admin ? (
            <div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '16px',
                  marginBottom: '20px',
                }}
              >
                {[
                  { label: 'Cedula', value: admin.cedula },
                  { label: 'Nombre', value: admin.nombre },
                  { label: 'Usuario', value: admin.usuario },
                  { label: 'Correo', value: admin.correo || '--' },
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      background: '#f9fafb',
                      borderRadius: '10px',
                      padding: '14px 18px',
                      border: '1px solid #f3f4f6',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#9ca3af',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        marginBottom: '4px',
                      }}
                    >
                      {item.label}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => setAdminEditMode(true)} style={C.btnPrimary}>
                <FiEdit3 size={14} style={{ marginRight: '6px' }} />
                Editar admin
              </button>
            </div>
          ) : (
            <div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Cedula *</label>
                  <input
                    value={admForm.cedula}
                    onChange={(e) => setAdmForm({ ...admForm, cedula: e.target.value })}
                    style={C.formInput}
                  />
                </div>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Nombre completo *</label>
                  <input
                    value={admForm.nombre}
                    onChange={(e) => setAdmForm({ ...admForm, nombre: e.target.value })}
                    style={C.formInput}
                  />
                </div>
              </div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Usuario *</label>
                  <input
                    value={admForm.usuario}
                    onChange={(e) => setAdmForm({ ...admForm, usuario: e.target.value })}
                    style={C.formInput}
                  />
                </div>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Clave{!admin ? ' *' : ''}</label>
                  <input
                    type="password"
                    value={admForm.clave}
                    onChange={(e) => setAdmForm({ ...admForm, clave: e.target.value })}
                    style={C.formInput}
                    placeholder={admin ? 'Dejar vacio para mantener' : ''}
                  />
                </div>
              </div>
              <div className="ec-form-row" style={C.formRow}>
                <div style={C.formGroup}>
                  <label style={C.formLabel}>Correo</label>
                  <input
                    type="email"
                    value={admForm.correo}
                    onChange={(e) => setAdmForm({ ...admForm, correo: e.target.value })}
                    style={C.formInput}
                  />
                </div>
                <div style={C.formGroup}></div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={saveAdmin}
                  disabled={savingAdmin}
                  style={{ ...C.btnPrimary, opacity: savingAdmin ? 0.6 : 1 }}
                >
                  <FiSave size={14} style={{ marginRight: '6px' }} />
                  {admin ? 'Actualizar admin' : 'Crear admin'}
                </button>
                {admin && (
                  <button
                    onClick={() => {
                      setAdminEditMode(false);
                      setAdmForm({ ...admForm, clave: '' });
                    }}
                    style={C.btnOutline}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* === SKILL MODAL === */}
      {skillModal && (
        <div style={C.skillModalOverlay} onClick={() => setSkillModal(null)}>
          <div className="ec-skill-modal" style={C.skillModal} onClick={(e) => e.stopPropagation()}>
            <div style={C.skillModalHeader}>
              <div style={C.skillModalTitle}>
                {skillForm.id || skillForm.source_key ? 'Editar Skill' : 'Nuevo Skill'}
              </div>
              <button onClick={() => setSkillModal(null)} style={C.skillModalClose}>
                <FiX size={22} />
              </button>
            </div>
            <div style={C.skillModalBody}>
              <div style={C.formGroup}>
                <label style={C.formLabel}>Titulo del skill</label>
                <input
                  value={skillForm.titulo}
                  onChange={(e) => setSkillForm({ ...skillForm, titulo: e.target.value })}
                  style={C.formInput}
                  placeholder="Ej: Ingredientes de la pizza margarita"
                  autoFocus
                />
              </div>
              <div style={{ ...C.formGroup, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <label style={C.formLabel}>Contenido</label>
                <textarea
                  value={skillForm.contenido}
                  onChange={(e) => setSkillForm({ ...skillForm, contenido: e.target.value })}
                  style={C.skillModalTextarea}
                  placeholder="Escribe el contenido del skill aqui..."
                />
              </div>
            </div>
            <div style={C.skillModalFooter}>
              <button onClick={() => setSkillModal(null)} style={C.btnOutline}>
                Cancelar
              </button>
              <button
                onClick={saveSkill}
                disabled={savingSkill}
                style={{ ...C.btnPrimary, opacity: savingSkill ? 0.6 : 1 }}
              >
                <FiSave size={14} style={{ marginRight: '6px' }} />
                {skillForm.id || skillForm.source_key ? 'Guardar cambios' : 'Crear skill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
